import React, { useEffect, useRef, useState } from "react";
import { API } from "../loads/common";

/** Small building blocks shared by the truck / load details panels (icons and markup follow iTruck_Search_Unified.html). */

const PATHS = {
  phone: "M5 3h4l2 5-3 2a16 16 0 0 0 6 6l2-3 5 2v4c0 2-2 2-3 2A19 19 0 0 1 3 6c0-1 0-3 2-3Z",
  mail: "M3 5h18v14H3ZM3 5l9 7 9-7",
  doc: "M5 3h9l5 5v13H5ZM14 3v6h5M8 13h8M8 17h6",
  pin: "M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0ZM10 10h4",
  chat: "M4 4h16v13H9l-5 4ZM8 9h8M8 13h5",
  truck: "M2 5h12v12H2ZM14 10h4l4 5v2h-8M5 17v3M18 17v3",
  shield: "M12 2l9 4v6c0 5-9 10-9 10S3 17 3 12V6ZM8 11l3 3 5-6",
};

export const DIcon = ({ name }) => (
  <svg aria-hidden="true" className="dt-icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24">
    <path d={PATHS[name] || PATHS.doc} />
  </svg>
);

export const Verified = () => (
  <span aria-label="Проверено iTruck" className="dt-verified" role="img" title="Проверено iTruck">
    ✓
  </span>
);

/** Django returns image paths; make them absolute against the API origin. */
export const absUrl = (u) => {
  if (!u) return null;
  if (/^(https?:|data:|blob:)/i.test(u)) return u;
  try {
    return new URL(u, new URL(API || "/", window.location.href).origin).href;
  } catch (e) {
    return u;
  }
};

/** Documents that really exist on a company profile. */
export const companyDocs = (co) =>
  co
    ? [
        ["Лицензия", co.license],
        ["Свидетельство", co.certificate],
        ["Страховой полис", co.insurance],
      ]
        .filter(([, u]) => !!u)
        .map(([name, u]) => ({ name, url: absUrl(u) }))
    : [];

export const phoneDigits = (p) => String(p || "").replace(/\D/g, "");
export const telHref = (p) => `tel:${String(p || "").replace(/[^\d+]/g, "")}`;
export const waHref = (p) => (phoneDigits(p).length >= 7 ? `https://wa.me/${phoneDigits(p)}` : null);

/** `<dl>` of label/value pairs; null when there is nothing to show. */
export function Pairs({ rows }) {
  const list = rows.filter((r) => r && r[1] != null && r[1] !== "" && r[1] !== false);
  if (!list.length) return null;
  return (
    <dl className="dt-pairs">
      {list.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Box with an icon heading and two columns of pairs (renders nothing when both are empty). */
export function InfoBox({ icon, title, left, right }) {
  const has = (rows) => rows.some((r) => r && r[1] != null && r[1] !== "" && r[1] !== false);
  if (!has(left) && !has(right)) return null;
  return (
    <section className="dt-box">
      <h3>
        {icon ? <DIcon name={icon} /> : null}
        {title}
      </h3>
      <div className="dt-columns">
        <Pairs rows={left} />
        <Pairs rows={right} />
      </div>
    </section>
  );
}

/** «Проверка iTruck» — only for verified companies; every item is backed by data. */
export function VerificationBox({ company, phone }) {
  if (!company?.verified) return null;
  const items = ["Компания подтверждена", phone ? "Телефон указан" : null].filter(Boolean);
  return (
    <section className="dt-verification">
      <h3>
        <DIcon name="shield" />
        Проверка iTruck
      </h3>
      <ul>
        {items.map((x) => (
          <li key={x}>
            <span aria-hidden="true">✓</span>
            {x}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Full-screen viewer for a company document (an image URL). */
function DocViewer({ doc, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector("button")?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="dt-doc-overlay" data-doc-overlay onMouseDown={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label={doc.name}>
      <section ref={ref}>
        <header>
          <h3>{doc.name}</h3>
          <button aria-label="Закрыть просмотр документа" onClick={onClose} type="button">
            ×
          </button>
        </header>
        <div className="dt-doc-view">
          <img alt={doc.name} src={doc.url} />
        </div>
        <a className="button" href={doc.url} rel="noopener noreferrer" target="_blank">
          Открыть в новой вкладке
        </a>
      </section>
    </div>
  );
}

/** «Документы компании»: only documents that exist on the profile; an image opens in a modal. */
export function DocsSection({ docs }) {
  const [open, setOpen] = useState(null);
  return (
    <section className="dt-docs">
      <header>
        <h3>Документы компании</h3>
      </header>
      {docs.length ? (
        docs.map((d) => (
          <button className="dt-doc" key={d.name} onClick={() => setOpen(d)} type="button">
            <DIcon name="doc" />
            <span>{d.name}</span>
            <b aria-hidden="true">›</b>
          </button>
        ))
      ) : (
        <p>Документы не предоставлены.</p>
      )}
      {open ? <DocViewer doc={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

/** One line with an icon: plain text, or a link when `href` is given. */
export const ContactLine = ({ icon, href, children, external }) =>
  children ? (
    <div className="dt-line">
      <DIcon name={icon} />
      {href ? (
        <a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
          {children}
        </a>
      ) : (
        <span>{children}</span>
      )}
    </div>
  ) : null;
